import { CircularProgress, Stack } from '@mui/material'
import { eventWithTime } from '@rrweb/types'
import { DateTime } from 'luxon'
import { type FC, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useEngagement, useEngagementActivities, useEngagementRecordings, useLocationCameras, useRecordingSegments } from '../../../../api/apiQueries'
import { RecordingType } from '../../../../api/models'
import { HttpError } from '../../../../api/HttpError'
import { Banner, BannerType } from '../../../../components/Banner/Banner'
import { LoggerName, namedLogger } from '../../../../utils/logger'
import { EngagementDetails } from '../EngagementSidePanel/EngagementDetails'
import { SidePanel } from '../EngagementSidePanel/SidePanel'
import { NoEngagementDataSidePanel, SidePanelSkeleton } from '../EngagementSidePanel/SidePanelStates'
import { assembleAndValidateRecordingsFromSegments } from '../ertUtils'
import { processActivities } from '../activityUtils'
import {
  getAgentViewStartEpochByRecordingId,
  getEngagementEndEpoch,
  getRecordingViewedDetails,
  isInspectViewingEvent,
  isRecordingStartedOrStoppedEvent,
  markViewedDetailsPastVideoCutoff,
  SPECIALIST_VIDEO_CUTOFF_MS
} from './utils/ertInspectUtils'
import { ErtInspectErrorBanner } from './ErtInspectErrorBanner'
import { useClipMediaDurations } from './useClipMediaDurations'
import { type EngagementRecordingVideoElement, InspectPlayer, type InspectPlayerHandle } from './InspectPlayer'
import { InspectTimelines } from './Timeline/InspectTimelines'
import { PlayerPlaceholder } from './PlayerPlaceholder'
import type { RecordingViewedDetails } from './types'
import { useSidePanelOutletContext } from '../../../../hooks/useSidePanelOutletContext'
import {
  getEngagementRecordings,
  applyClipMediaDurations,
  capRecordingsAtEngagementEnd,
  getCameraEntriesInDisplayOrder
} from './utils/recordingsUtils'

const logger = namedLogger(LoggerName.ErtInspect)

export const ErtInspect: FC = () => {
  const { sid: locationId, engagementId } = useParams()
  const { setSidePanelAddOn } = useSidePanelOutletContext()
  const { data: engagement, isError: engagementIsError, isLoading: engagementIsLoading } = useEngagement(engagementId)
  const { data: activities, isLoading: activitiesIsLoading } = useEngagementActivities(engagementId)
  const {
    data: recordings,
    isError: recordingsIsError,
    isLoading: recordingsIsLoading,
    error: recordingsError
  } = useEngagementRecordings(locationId && engagementId ? { locationId, engagementId: engagementId } : undefined)
  const { data: allCameras = [] } = useLocationCameras(locationId)
  // Load the web rrweb recording to derive which cameras/clips the specialist viewed.
  const webRecordingSegments = useRecordingSegments(engagementId, RecordingType.web).data

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true)
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null)

  // The per-camera playing video elements, mirrored from InspectPlayer so the timelines' live scrubber
  // can track playback. (InspectPlayer also exposes this on its imperative handle, but that isn't reactive.)
  const [videoElements, setVideoElements] = useState<Map<string, EngagementRecordingVideoElement>>(new Map())
  const [rrwebEvents, setRrwebEvents] = useState<eventWithTime[]>([])

  // First/last event times of the FULL web recording, plus its recording start/stop markers. rrwebEvents
  // holds only the Inspect-filtered events so these can't be read off the array.
  const [rrwebEventTimeBounds, setRrwebEventTimeBounds] = useState<{
    timeOfFirstEvent: number
    timeOfLastEvent: number
    recordingStartedOrStoppedEvents: eventWithTime[]
  } | null>(null)
  const inspectPlayerRef = useRef<InspectPlayerHandle>(null)

  const baseEngagementRecordings = useMemo(() => (recordings ? getEngagementRecordings(recordings) : undefined), [recordings])

  // Per-clip real media durations probed from the media itself - the FLV tag stream or the HLS playlist
  const clipMediaDurationsByRecordingId = useClipMediaDurations(baseEngagementRecordings, locationId, engagementId)

  // Shrink clips to their probed real durations. Applying it here means every consumer - timeline
  // widths, seek mapping, viewed bar, controls duration - sees footage that actually exists.
  const engagementRecordings = useMemo(
    () => (baseEngagementRecordings ? applyClipMediaDurations(baseEngagementRecordings, clipMediaDurationsByRecordingId) : undefined),
    [baseEngagementRecordings, clipMediaDurationsByRecordingId]
  )
  const hasRecordings = useMemo(() => {
    return Array.from(engagementRecordings?.recordingsByCameraId.values() ?? []).some(clips => clips.length > 0)
  }, [engagementRecordings])

  const recordingsErrorCode = recordingsError instanceof HttpError ? recordingsError.status : 0

  // Every "nothing to play" case in one flag: the fetch failed, museum withheld the whole engagement,
  // or it resolved with no clips at all.
  const hasNoFootage = recordingsIsError || engagementRecordings?.accessAllowed === false || (!!engagementRecordings && !hasRecordings)

  const canExplainNoFootage = hasNoFootage && !!engagement && !!activities

  const isLoading = engagementIsLoading || activitiesIsLoading || recordingsIsLoading

  // Same processedActivities ErtQA uses (filtered, chronological, timing bounded to the recording), so the
  // side panel's duration/details match QA. ErtInspect doesn't run the full rrweb pipeline, so we hand
  // processActivities just the first/last event times it needs, read off the assembled events.
  const processedActivities = useMemo(
    () => processActivities(rrwebEventTimeBounds ?? { timeOfFirstEvent: 0, timeOfLastEvent: 0, recordingStartedOrStoppedEvents: [] }, activities),
    [rrwebEventTimeBounds, activities]
  )

  // When the engagement ended (last activity completed) - the timeline and playback stop here so QA review
  // only shows what the specialist saw. Null until activities resolve; consumers treat null as "no bound yet".
  const engagementEndEpoch = getEngagementEndEpoch(processedActivities)

  // Cap the timeline window at the engagement end (recordings can extend past it); clips are left intact -
  // playback truncation and the shortened window handle the rest. Museum itself caps recordings at the
  // CLEARANCE time, not at the specialist's 20-minute video cutoff (createdTimestamp + 20min).
  // Footage between the cutoff and clearance was never visible to the specialist, the viewed bar
  // shows it as NotAvailable (see viewedDetailsByRecordingId below).
  const cappedEngagementRecordings = useMemo(
    () => (engagementRecordings ? capRecordingsAtEngagementEnd(engagementRecordings, engagementEndEpoch) : undefined),
    [engagementRecordings, engagementEndEpoch]
  )

  const viewedDetailsByRecordingId = useMemo(() => {
    if (!engagementRecordings) {
      return new Map<string, RecordingViewedDetails[]>()
    }
    const clips = Array.from(engagementRecordings.recordingsByCameraId.values()).flat()
    const agentViewStartByRecordingId = getAgentViewStartEpochByRecordingId(engagement?.videoRecordings ?? [], clips)
    const viewedDetails = getRecordingViewedDetails(rrwebEvents, clips, agentViewStartByRecordingId, rrwebEventTimeBounds?.timeOfLastEvent)
    if (!engagement?.createdTimestamp) {
      return viewedDetails
    }
    // Footage past the specialist video cutoff (created + 20min) was hidden in Holodeck even though the
    // engagement (and its recordings) can run on to clearance - stripe it NotAvailable.
    const videoCutoffEpoch = DateTime.fromISO(engagement.createdTimestamp).toMillis() + SPECIALIST_VIDEO_CUTOFF_MS
    return markViewedDetailsPastVideoCutoff(viewedDetails, videoCutoffEpoch)
  }, [rrwebEvents, rrwebEventTimeBounds, engagementRecordings, engagement])

  const cameras = allCameras.filter(c => engagementRecordings?.recordingsByCameraId.has(c.uuid))

  // Clicking a camera's timeline selects it and seeks its video to the clicked position. Seek lives on
  // InspectPlayer's handle because reaching an arbitrary time may require switching which clip is active.
  const handleCameraTimelineClick = (cameraId: string, absoluteTimeMs: number) => {
    setSelectedCameraId(cameraId)
    inspectPlayerRef.current?.seekCamera(cameraId, absoluteTimeMs)
  }

  // Default to the first camera Inspect actually shows - the triggering camera, which is what the timelines
  // put on row 1 - and not whichever camera museum happened to key first in its recordings response. When
  // those disagree, hitting play starts a different camera than the one at the top of the timelines
  useEffect(() => {
    if (selectedCameraId === null && engagementRecordings && engagement) {
      const cameraEntries = getCameraEntriesInDisplayOrder(engagementRecordings.recordingsByCameraId, engagement.triggeringDevice.id)
      // Skip cameras museum returned with no clips at all - they mount no player, so playback controls
      // would have nothing to drive.
      const firstCameraId = cameraEntries.find(([, clips]) => clips.length > 0)?.[0]
      if (firstCameraId) {
        setSelectedCameraId(firstCameraId)
      }
    }
  }, [engagementRecordings, selectedCameraId, engagement])

  useEffect(() => {
    let cancelled = false
    const assembleEvents = async () => {
      if (!webRecordingSegments) {
        setRrwebEvents([])
        setRrwebEventTimeBounds(null)
        return
      }
      try {
        // Because we are filtering events, we can actually lose the first/last event times and the
        // recording start/stop markers, so we track them separately here while filtering to feed them
        // into processActivities.
        let timeOfFirstEvent = Number.POSITIVE_INFINITY
        let timeOfLastEvent = 0
        const recordingStartedOrStoppedEvents: eventWithTime[] = []
        const boundsTrackingInspectFilter = (event: eventWithTime) => {
          if (event.timestamp < timeOfFirstEvent) timeOfFirstEvent = event.timestamp
          if (event.timestamp > timeOfLastEvent) timeOfLastEvent = event.timestamp
          if (isRecordingStartedOrStoppedEvent(event)) recordingStartedOrStoppedEvents.push(event)
          return isInspectViewingEvent(event)
        }

        const webRecordings = await assembleAndValidateRecordingsFromSegments(webRecordingSegments, 0, boundsTrackingInspectFilter)
        if (!cancelled) {
          setRrwebEvents(webRecordings.flatMap(r => r.segments).flatMap(s => s.rrwebEvents ?? []))
          if (timeOfLastEvent > 0) {
            setRrwebEventTimeBounds({ timeOfFirstEvent, timeOfLastEvent, recordingStartedOrStoppedEvents })
          } else {
            setRrwebEventTimeBounds(null)
          }
        }
      } catch (e) {
        // viewed-details highlighting is non-critical - degrade to no highlighting rather than break the page
        logger.error('Failed to assemble rrweb recording for viewed details', e as Error)
        if (!cancelled) {
          setRrwebEvents([])
          setRrwebEventTimeBounds(null)
        }
      }
    }
    assembleEvents()
    return () => {
      cancelled = true
    }
  }, [webRecordingSegments])

  // This feeds into the parent ErtPage's slot for the side panel add-on.
  useEffect(() => {
    setSidePanelAddOn(
      engagementIsLoading || activitiesIsLoading ? (
        <SidePanelSkeleton />
      ) : engagement ? (
        <SidePanel testIdPrefix="engagement-" isOpen={isSidePanelOpen} onOpenChange={setIsSidePanelOpen}>
          <EngagementDetails engagement={engagement} activities={processedActivities} />
        </SidePanel>
      ) : (
        <NoEngagementDataSidePanel />
      )
    )
  }, [setSidePanelAddOn, engagement, processedActivities, activitiesIsLoading, engagementIsLoading, isSidePanelOpen])

  return (
    <Stack alignItems="center" gap="var(--spacing-m)" flex="1" width="100%" minHeight="0">
      {engagementIsError && <Banner type={BannerType.Error} title="Failed to load engagement" />}
      {isLoading ? (
        <Stack data-testid="ert-loading-spinner" sx={{ justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Stack>
      ) : canExplainNoFootage ? (
        <ErtInspectErrorBanner
          engagement={engagement}
          activities={activities}
          errorCode={recordingsErrorCode}
          disallowedReason={engagementRecordings?.disallowedReason}
        />
      ) : hasNoFootage ? (
        // Recordings are unavailable but the engagement/activities never loaded, so there is nothing to
        // explain it with
        <Banner
          type={BannerType.Error}
          title="Unable to load engagement recordings."
          sx={{ marginY: 'var(--spacing-l)' }}
          data-testid="recordings-error-banner"
        />
      ) : locationId && engagementId && engagementRecordings && hasRecordings ? (
        <InspectPlayer
          ref={inspectPlayerRef}
          cameras={cameras}
          recordingsByCameraId={engagementRecordings.recordingsByCameraId}
          selectedCameraId={selectedCameraId}
          isSidePanelOpen={isSidePanelOpen}
          onToggleSidePanel={() => setIsSidePanelOpen(prev => !prev)}
          onVideoElementsChange={setVideoElements}
          engagementEndEpoch={engagementEndEpoch}
          deniedReasonByCameraId={engagementRecordings.deniedReasonByCameraId}
        />
      ) : (
        // Loading shows the spinner above, and every settled recordings outcome lands in a banner or
        // the player - this is only reachable without route params (queries disabled, never loading).
        <PlayerPlaceholder />
      )}
      {cappedEngagementRecordings && engagement && (
        <InspectTimelines
          engagementRecordings={cappedEngagementRecordings}
          cameras={cameras}
          engagement={engagement}
          selectedCameraId={selectedCameraId}
          onCameraTimelineClick={handleCameraTimelineClick}
          onCameraSelect={setSelectedCameraId}
          viewedDetailsByRecordingId={viewedDetailsByRecordingId}
          videoElements={videoElements}
        />
      )}
    </Stack>
  )
}

ErtInspect.displayName = 'ErtInspect'
